/**
 * cast.js — 起卦
 *
 * 关键点：铜钱法与蓍草法的概率分布**不一样**，
 * 随手写个 Math.random() 挑 64 卦更是两者都不是。
 *
 *              老阴6   少阳7   少阴8   老阳9
 *   铜钱法      1/8     3/8     3/8     1/8      （阴变阳、阳变阴等概率）
 *   大衍蓍草    1/16    5/16    7/16    3/16     （阳变的概率是阴变的三倍）
 *
 * 蓍草法是《系辞》所载古法，阳爻更「躁动」，这在断卦时是有意义的差别。
 * 本模块两种都实现了，蓍草法是真的模拟分二挂一揲四，不是查表凑概率。
 */

/**
 * mulberry32 伪随机，给定种子可复现（做测试、以及「同一时刻同一问只出一卦」）。
 * 选它是因为它通过了 gjrand 的基本统计检验，
 * 而随手写的 xorshift 变体常常在低位上有偏，会把卦的概率分布带歪。
 */
export function seededRandom(seed = Date.now()) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 默认随机源：浏览器/Node 的密码学随机数，退化到 Math.random */
export function systemRandom() {
  const g = globalThis;
  if (g.crypto && g.crypto.getRandomValues) {
    return () => {
      const buf = new Uint32Array(1);
      g.crypto.getRandomValues(buf);
      return buf[0] / 4294967296;
    };
  }
  return Math.random;
}

/* ------------------------------------------------------- 铜钱法 */

/**
 * 三枚铜钱掷一次 → 一爻。
 * 记法：背（无字面）为 3，字（有字面）为 2，三枚相加。
 * 6=老阴(变) 7=少阳 8=少阴 9=老阳(变)
 */
export function coinToss(rand) {
  const coins = [0, 0, 0].map(() => (rand() < 0.5 ? 3 : 2));
  return { value: coins.reduce((a, b) => a + b, 0), coins };
}

/* ------------------------------------------------------- 蓍草法 */

/**
 * 大衍筮法的一「变」：分而为二 → 挂一 → 揲之以四 → 归奇于扐。
 * 返回本次取走的策数（第一变 5 或 9，第二三变 4 或 8）。
 *
 * split 说明：
 *  'balanced'（默认）——「分二」时四个余数类等概率。
 *      这是《系辞》推演出经典概率 老阳3/16 少阴7/16 少阳5/16 老阴1/16 的前提。
 *  'uniform' ——分点在 1..n-1 上均匀。物理上更「老实」，
 *      但第二三变的堆数不是 4 的倍数，会带来约 1% 的偏差（老阳偏高）。
 */
function oneChange(total, rand, split) {
  let left;
  if (split === 'uniform') {
    left = 1 + Math.floor(rand() * (total - 1));
  } else {
    // 先均匀选一个余数类，再在该类中均匀选分点
    const r = Math.floor(rand() * 4);
    const candidates = [];
    for (let i = 1; i <= total - 1; i += 1) if (i % 4 === r) candidates.push(i);
    left = candidates[Math.floor(rand() * candidates.length)];
  }
  let right = total - left;
  right -= 1; // 挂一
  const gua = 1;
  const leftRem = left % 4 === 0 ? 4 : left % 4;
  const rightRem = right % 4 === 0 ? 4 : right % 4;
  const taken = gua + leftRem + rightRem;
  return { taken, remaining: total - taken };
}

/** 三变成一爻 */
export function yarrowToss(rand, split = 'balanced') {
  let total = 49;
  const changes = [];
  for (let i = 0; i < 3; i += 1) {
    const r = oneChange(total, rand, split);
    changes.push(r.taken);
    total = r.remaining;
  }
  const value = total / 4; // 6/7/8/9
  return { value, changes, stalks: total };
}

/* --------------------------------------------------------- 起卦 */

const METHODS = {
  coin: coinToss,
  yarrow: yarrowToss
};

/**
 * 起一卦。
 * @param {object} opts
 *   method  'coin'（默认，铜钱法/火珠林法）| 'yarrow'（大衍筮法）| 'manual'
 *   values  method==='manual' 时传入六个 6/7/8/9，自下而上
 *   seed    传入则可复现
 * @returns { values, lines, changedLines, moving, tosses, method }
 */
export function cast(opts = {}) {
  const { method = 'coin', values, seed } = opts;
  const rand = seed === undefined ? systemRandom() : seededRandom(seed);

  let vals;
  let tosses = [];
  if (method === 'manual') {
    if (!Array.isArray(values) || values.length !== 6) throw new Error('manual 需要六个爻值');
    vals = values.map((v) => {
      if (![6, 7, 8, 9].includes(v)) throw new Error(`爻值必须是 6/7/8/9，收到 ${v}`);
      return v;
    });
  } else {
    const toss = METHODS[method];
    if (!toss) throw new Error(`未知起卦法：${method}`);
    tosses = Array.from({ length: 6 }, () => toss(rand));
    vals = tosses.map((t) => t.value);
  }

  const lines = vals.map((v) => (v === 7 || v === 9 ? 1 : 0));
  const changedLines = vals.map((v, i) => {
    if (v === 9) return 0; // 老阳变阴
    if (v === 6) return 1; // 老阴变阳
    return lines[i];
  });
  const moving = vals.map((v, i) => (v === 6 || v === 9 ? i + 1 : 0)).filter(Boolean);

  return { values: vals, lines, changedLines, moving, tosses, method };
}

/** 爻值 → 中文称谓 */
export const VALUE_LABEL = { 6: '老阴', 7: '少阳', 8: '少阴', 9: '老阳' };
